import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, ChartLineUp, Flag, Image as ImageIcon, Check, X } from "@phosphor-icons/react";

export default function AdminForumPage() {
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pending, setPending] = useState([]);

  const load = () => {
    api.get("/forum/admin/reports").then(({ data }) => setReports(data.items)).catch(() => toast.error("Super-admin only."));
    api.get("/forum/admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get("/forum/admin/pending-images").then(({ data }) => setPending(data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id) => { await api.post(`/forum/admin/reports/${id}/resolve`); load(); };
  const banUser = async (uid) => { if (!confirm("Ban this user?")) return; await api.post(`/forum/admin/ban/${uid}`); toast.success("Toggled"); };
  const approveImage = async (postId) => {
    try { await api.post(`/forum/admin/approve-image/${postId}`); toast.success("Image approved — it's now live."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't approve."); }
  };
  const rejectImage = async (postId) => {
    if (!confirm("Reject this image? The post's text will stay live, only the image is removed.")) return;
    try { await api.post(`/forum/admin/reject-image/${postId}`); toast.success("Image rejected — text remains."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reject."); }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8]"><Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-forum-page">
        <div className="text-[10px] uppercase tracking-[0.25em] text-gold flex items-center gap-1.5"><ShieldCheck size={14} weight="duotone" /> Super-Admin · Forum</div>
        <h1 className="font-serif-display text-3xl mt-2">Community Moderation</h1>

        {analytics && (
          <section className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="forum-analytics">
            {[
              ["Posts (total)", analytics.totals.posts],
              ["Posts · 7d", analytics.totals.posts_7d],
              ["Comments · 7d", analytics.totals.comments_7d],
              ["Likes · 7d", analytics.totals.likes_7d],
            ].map(([k, v]) => (
              <div key={k} className="card-surface rounded-xl p-3.5">
                <div className="text-[10px] uppercase tracking-widest text-muted">{k}</div>
                <div className="font-serif-display text-2xl mt-1.5">{v}</div>
              </div>
            ))}
            <div className="card-surface rounded-xl p-3.5 col-span-2 sm:col-span-4">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2 flex items-center gap-1"><ChartLineUp size={11} weight="duotone" /> Daily active users (last 7 days)</div>
              <div className="flex items-end gap-1.5 h-20">
                {analytics.dau_7d.map((d) => {
                  const max = Math.max(...analytics.dau_7d.map((x) => x.active_users), 1);
                  const h = Math.max(4, (d.active_users / max) * 72);
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                      <div className="bg-gold/70 rounded-t w-full" style={{ height: h }} title={`${d.active_users} active`}></div>
                      <span className="text-[9px] text-muted">{d.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="card-surface rounded-xl p-3.5 col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Top tags · 30d</div>
              <div className="flex flex-wrap gap-1.5">
                {analytics.top_tags.map((t) => <span key={t.tag} className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-900">#{t.tag} · {t.count}</span>)}
              </div>
            </div>
            <div className="card-surface rounded-xl p-3.5 col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Top authors · 30d</div>
              <div className="space-y-1.5">
                {analytics.top_authors.slice(0, 5).map((a) => (
                  <div key={a.user_id} className="flex items-center justify-between text-sm">
                    <span>{a.name}</span>
                    <span className="text-xs text-muted">{a.posts} posts · {a.likes} likes</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="mt-7" data-testid="pending-images-section">
          <h2 className="font-serif-display text-xl mb-3 flex items-center gap-2">
            <ImageIcon size={16} weight="duotone" /> Pending image approvals · {pending.length}
          </h2>
          {pending.length === 0 && (
            <p className="text-sm text-muted">All clear — no images waiting for review.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {pending.map((p) => (
              <div key={p.id} className="card-surface rounded-xl p-4" data-testid={`pending-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted">by <b>{p.name}</b> · {new Date(p.created_at).toLocaleString()}</div>
                    {p.group_slug && <div className="text-[10px] uppercase tracking-widest text-amber-700 mt-0.5">{p.group_slug}</div>}
                    <div className="font-medium mt-1 leading-snug">{p.title}</div>
                    <p className="text-xs text-stone-600 mt-1 line-clamp-3">{p.body}</p>
                  </div>
                </div>
                {p.image_b64 && (
                  <img
                    src={p.image_b64.startsWith("data:") ? p.image_b64 : `data:image/jpeg;base64,${p.image_b64}`}
                    alt=""
                    className="mt-3 rounded-lg max-h-64 w-full object-cover border border-soft"
                  />
                )}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => approveImage(p.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs font-semibold hover:bg-emerald-200" data-testid={`approve-${p.id}`}>
                    <Check size={12} weight="bold" /> Approve
                  </button>
                  <button onClick={() => rejectImage(p.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-rose-100 text-rose-900 text-xs font-semibold hover:bg-rose-200" data-testid={`reject-${p.id}`}>
                    <X size={12} weight="bold" /> Reject image
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-7" data-testid="forum-reports">
          <h2 className="font-serif-display text-xl mb-3 flex items-center gap-2"><Flag size={16} weight="duotone" /> Open reports · {reports.length}</h2>
          {reports.length === 0 && <p className="text-sm text-muted">No open reports. Community is calm 🙏</p>}
          <div className="space-y-3">
            {reports.map((r) => (
              <div key={r.id} className="card-surface rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-900">{r.target_kind.toUpperCase()}</span>
                  <span className="text-xs text-muted">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm mt-2"><b>Reason:</b> {r.reason}</p>
                {r.target && (
                  <div className="mt-3 rounded-lg bg-bg/40 p-3 text-sm">
                    <div className="text-xs text-muted">by {r.target.name}</div>
                    {r.target.title && <div className="font-medium mt-0.5">{r.target.title}</div>}
                    <p className="text-xs mt-1 line-clamp-3">{r.target.body}</p>
                    {r.target_kind === "post" && <Link to={`/forum/post/${r.target.id}`} className="text-xs text-gold mt-2 inline-block">View post →</Link>}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => resolve(r.id)} className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs font-medium">Resolve</button>
                  {r.target && <button onClick={() => banUser(r.target.author_id)} className="px-3 py-1.5 rounded-full bg-rose-100 text-rose-900 text-xs font-medium">Ban author</button>}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
